import { BUG_SEVERITIES, type BugReportInput, type BugSeverity } from "@kontaktfix/shared";
import { Button, Dialog, Field, Select, Textarea } from "@kontaktfix/ui";
import { BugIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { recentClientErrors } from "@/lib/client-errors";
import { toastError } from "@/lib/errors";
import { getTheme } from "@/lib/theme";

function context(): BugReportInput["context"] {
  return {
    pathname: window.location.pathname,
    userAgent: navigator.userAgent.slice(0, 500),
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    viewport: `${window.innerWidth} x ${window.innerHeight}`,
    theme: getTheme(),
    errors: recentClientErrors(),
  };
}

/** "Report a problem": on every signed-in page of the app and in /ops. */
export function BugReportButton({ endpoint }: { endpoint: "/api/bug-reports" | "/api/ops/bug-reports" }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [tried, setTried] = useState("");
  const [happened, setHappened] = useState("");
  const [severity, setSeverity] = useState<BugSeverity>("annoying");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(endpoint, { tried, happened, severity, context: context() });
      toast.success(t("bugReport.sent"));
      setOpen(false);
      setTried("");
      setHappened("");
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex h-8 items-center gap-2.5 rounded-md px-2.5 text-[13px] font-medium text-fg-muted transition-colors hover:bg-muted/60 hover:text-fg [&_svg]:size-4"
      >
        <BugIcon />
        {t("bugReport.button")}
      </button>
      <Dialog
        open={open}
        onOpenChange={setOpen}
        title={t("bugReport.title")}
        description={t("bugReport.description")}
        footer={
          <>
            <Button onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
            <Button
              variant="primary"
              loading={busy}
              disabled={!tried.trim() || !happened.trim()}
              onClick={submit}
            >
              {t("bugReport.send")}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <Field label={t("bugReport.tried")}>
            <Textarea value={tried} maxLength={2000} onChange={(e) => setTried(e.target.value)} autoFocus />
          </Field>
          <Field label={t("bugReport.happened")}>
            <Textarea value={happened} maxLength={4000} onChange={(e) => setHappened(e.target.value)} />
          </Field>
          <Field label={t("bugReport.severity")}>
            <Select value={severity} onChange={(e) => setSeverity(e.target.value as BugSeverity)}>
              {BUG_SEVERITIES.map((s) => (
                <option key={s} value={s}>
                  {t(`bugReport.severities.${s}`)}
                </option>
              ))}
            </Select>
          </Field>
          <p className="text-[12px] text-fg-subtle">{t("bugReport.privacy")}</p>
        </div>
      </Dialog>
    </>
  );
}
